import React, { useEffect, useMemo, useState } from 'react';
import { adminServices } from '../services/adminServices';

const ISSUE_TYPE_OPTIONS = [
  { value: 'ui', label: 'UI / layout' },
  { value: 'permissions', label: 'Permissions / access' },
  { value: 'workflow', label: 'Workflow / blocked step' },
  { value: 'email', label: 'Email / notification' },
  { value: 'other', label: 'Other' },
];

function formatContextValue(value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export default function SupportIssueModal({
  isOpen,
  onClose,
  sourceSurface,
  context = {},
  currentUser = null,
  permissionSnapshot = null,
}) {
  const [issueType, setIssueType] = useState('ui');
  const [attemptedAction, setAttemptedAction] = useState('');
  const [errorSummary, setErrorSummary] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedIssueId, setSubmittedIssueId] = useState('');
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setIssueType('ui');
    setAttemptedAction('');
    setErrorSummary('');
    setDescription('');
    setIsSubmitting(false);
    setSubmittedIssueId('');
    setSubmitError('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const contextRows = useMemo(
    () =>
      [
        ['Surface', sourceSurface],
        ['Route', context.route],
        ['School', context.schoolName || context.schoolId],
        ['Report', context.reportId],
        ['Workflow', context.workflowStep],
        ['QA phase', context.qaPhase],
        ['Language', context.language],
        ['Current view', context.portalView || context.view],
      ].filter(([, value]) => formatContextValue(value)),
    [context, sourceSurface]
  );

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!description.trim()) {
      setSubmitError('Please describe what happened.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const issueId = await adminServices.createSupportIssue({
        issueType,
        attemptedAction,
        errorSummary,
        description,
        sourceSurface,
        route:
          typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}`
            : context.route || '',
        schoolId: context.schoolId || null,
        schoolName: context.schoolName || null,
        reportId: context.reportId || null,
        workflowStep: context.workflowStep || null,
        qaPhase: context.qaPhase || null,
        language: context.language || null,
        reporter: {
          uid: currentUser?.uid || context.reporterUid || null,
          email: currentUser?.email || context.reporterEmail || null,
          displayName:
            currentUser?.displayName ||
            context.reporterName ||
            context.schoolName ||
            context.schoolId ||
            'Unknown reporter',
          actorType: context.actorType || (currentUser ? 'staff' : 'portal_user'),
          role: context.role || null,
        },
        reporterPermissions: permissionSnapshot || context.permissionSnapshot || null,
        context,
      });
      setSubmittedIssueId(issueId);
    } catch (err) {
      setSubmitError(err?.message || 'Unable to send your report right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      onClick={() => onClose?.()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 6000,
        background: 'rgba(2, 6, 23, 0.7)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(720px, 100%)',
          maxHeight: 'min(88vh, 900px)',
          overflow: 'auto',
          borderRadius: 24,
          background: '#0f172a',
          color: 'white',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.45)',
        }}
      >
        <div
          style={{
            padding: '22px 24px 18px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 20,
          }}
        >
          <div>
            <div style={{ fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.6 }}>
              Help
            </div>
            <h3 style={{ margin: '6px 0 0 0', fontSize: '1.35rem' }}>Report a problem</h3>
            <p style={{ margin: '10px 0 0 0', opacity: 0.75, lineHeight: 1.5, fontSize: '0.94rem' }}>
              Send the issue exactly as you are experiencing it. IFIP support will see your reported context, not just their own session.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            style={{
              background: 'transparent',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 10,
              width: 36,
              height: 36,
              cursor: 'pointer',
              fontSize: '1.1rem',
              flex: '0 0 auto',
            }}
            aria-label="Close help dialog"
          >
            x
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {submittedIssueId ? (
            <div
              style={{
                borderRadius: 18,
                padding: 20,
                background: 'rgba(19, 138, 138, 0.12)',
                border: '1px solid rgba(19, 138, 138, 0.45)',
              }}
            >
              <h4 style={{ margin: '0 0 10px 0' }}>Report sent</h4>
              <p style={{ margin: 0, opacity: 0.85, lineHeight: 1.55 }}>
                Your issue has been queued for review. Reference: <strong>{submittedIssueId}</strong>
              </p>
              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => onClose?.()}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 12,
                    border: 'none',
                    background: '#138a8a',
                    color: 'white',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                {contextRows.map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 14,
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.55 }}>
                      {label}
                    </div>
                    <div style={{ marginTop: 6, fontSize: '0.9rem', lineHeight: 1.4, wordBreak: 'break-word' }}>
                      {formatContextValue(value)}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gap: 16 }}>
                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: '0.86rem', opacity: 0.82 }}>Issue type</span>
                  <select
                    value={issueType}
                    onChange={(event) => setIssueType(event.target.value)}
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 12,
                      color: 'white',
                      padding: '12px 14px',
                    }}
                  >
                    {ISSUE_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value} style={{ color: '#111827' }}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: '0.86rem', opacity: 0.82 }}>What were you trying to do?</span>
                  <input
                    type="text"
                    value={attemptedAction}
                    onChange={(event) => setAttemptedAction(event.target.value)}
                    placeholder="e.g. Submit report for peer review"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 12,
                      color: 'white',
                      padding: '12px 14px',
                    }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: '0.86rem', opacity: 0.82 }}>Visible error message</span>
                  <input
                    type="text"
                    value={errorSummary}
                    onChange={(event) => setErrorSummary(event.target.value)}
                    placeholder="Paste any message you can see"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 12,
                      color: 'white',
                      padding: '12px 14px',
                    }}
                  />
                </label>

                <label style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: '0.86rem', opacity: 0.82 }}>Describe what happened</span>
                  <textarea
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder="What did you expect, and what happened instead?"
                    rows={6}
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 16,
                      color: 'white',
                      padding: '14px 16px',
                      resize: 'vertical',
                      minHeight: 140,
                    }}
                  />
                </label>
              </div>

              {submitError ? (
                <div
                  style={{
                    marginTop: 16,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'rgba(239, 68, 68, 0.12)',
                    border: '1px solid rgba(239, 68, 68, 0.36)',
                    color: '#fecaca',
                  }}
                >
                  {submitError}
                </div>
              ) : null}

              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => onClose?.()}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'transparent',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 12,
                    border: 'none',
                    background: '#138a8a',
                    color: 'white',
                    fontWeight: 700,
                    cursor: isSubmitting ? 'wait' : 'pointer',
                    opacity: isSubmitting ? 0.75 : 1,
                  }}
                >
                  {isSubmitting ? 'Sending...' : 'Send report'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
